import LRU from "lru-cache";
import siteConfig from "site-config";
import { formatMessage } from "lib/globalize-util";
import { getStore } from "store";
import { buildStatement, IClauseType, AggregationFunctions } from "helpers/merchant/lookup/statement-builder";
import {
    ILookupWhere,
    ILookupTerm,
    ILookup,
    ILookupWhereField,
    ILookupFromType,
    ILookupFieldType,
    ILookupRowObject,
    ILookupRowItem,
    IDependency,
} from "helpers/merchant/lookup/types";
import { nullish } from "helpers/nullish";
import { APIError } from "lib/errors";
import { SizingErrors, Kinds, ValueTypes, SEMICOLON_DELIMITER, COMMA_DELIMITER } from "constants/product-collection";
import { get, template } from "lodash";
import { Map } from "immutable";
import { IDeferredPromise, deferredPromise } from "lib/promise";
import { internalGetter } from "helpers/product-collection";

const recordsPerPage = 1000;

interface ISelectType {
    terms: ILookupTerm[];
}

export interface ILookuoSizeCurvesParmas {
    data: { [key: string]: number | string };
    where?: ILookupWhere;
    offset?: number;
    rows?: object[];
    merchantId?: string;
    fieldName?: string;
    filterFn?: (row: any) => boolean;
    fieldToValueTypeMap?: Map<string, ValueTypes>;
}

interface ILookupResponseRowValue {
    int64_value?: {
        value: number;
    };
    string_value?: {
        value: string;
    };
}

const getSchemaKey = (tableColumn: string, select?: ISelectType) => {
    if (!select) {
        return { name: tableColumn };
    }

    const term = get(select, "terms", []).find((it) => it.column.table_column === tableColumn) || {};
    const kind = get(term, "column.kind", "");
    return {
        name: kind && kind !== Kinds.DEFAULT_KIND ? kind : tableColumn,
        delimiter: get(term, "string_aggr.delimiter", ""),
        schemaName: get(term, "column.schema_column", ""),
    };
};

export const transformLookupResponse = (response: any, select?: ISelectType): ILookupRowItem[] => {
    if (!response || !response.metadata || !response.rows) {
        return response;
    }

    return response.rows.map((row) =>
        response.metadata.row_type.fields.reduce((acc, schema, index) => {
            const { name, delimiter, schemaName } = getSchemaKey(schema.name, select);

            const value = getLookupValueByType(row.values[index], schema.type.code);
            const valueObj: any = {};
            if (schema.type.code === ILookupFieldType.INT64) {
                valueObj.int64_value = value;
            } else {
                valueObj.string_value = value;
            }

            return {
                ...acc,
                [name]: {
                    ...valueObj,
                    value: delimiter ? String(value).split(delimiter) : value,
                    type: schema.type.code,
                    schemaName,
                },
            };
        }, {}),
    );
};

const getFilterRules = (
    data: object,
    fields: ILookupWhereField[],
    fieldToValueTypeMap?: Map<string, ValueTypes>,
): IClauseType[] => {
    if (!fields || !fields.length) {
        return [];
    }

    return fields.map(({ column }) => ({
        field: column.table_column,
        value: !column.enforced_value ? data[column.schema_column] : column.enforced_value,
        operator: "eq",
        value_type: fieldToValueTypeMap?.get(column.schema_column),
    }));
};

const verifyOrCalculateFilters = (data: object, where: ILookupWhere, fieldToValueTypeMap?: Map<string, ValueTypes>) => {
    if (!where || !where.fields) {
        throw new APIError("All retries were run without any match", null, SizingErrors.NoMatch);
    }
    const rules = getFilterRules(data, where.fields, fieldToValueTypeMap);
    return rules.every(({ value }) => !nullish(value) && value !== "")
        ? { rules, where }
        : verifyOrCalculateFilters(data, where.fallback, fieldToValueTypeMap);
};

let _executeSqlResultCache;
export const getExecuteSqlResultCache = () => {
    if (!_executeSqlResultCache) {
        _executeSqlResultCache = new LRU({
            // Arbitrary number. 1000 entries seem good enough, assuming 5 lookups per row, 200 rows
            max: 1000,
            // 5 minutes
            ttl: 1000 * 60 * 5,
        });
    }

    return _executeSqlResultCache;
};
const generateRequestPromise = (brandId: string, request: any): Promise<any> => {
    const sizeCurveResultCache = getExecuteSqlResultCache();
    return new Promise((resolve, reject) => {
        const requestKey = JSON.stringify(request);
        if (sizeCurveResultCache.has(requestKey)) {
            resolve(sizeCurveResultCache.get(requestKey));
        } else {
            fetch(`${siteConfig.goAPI}/v1/merchanttable/executesql`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    Module: "base",
                    "X-Requested-With": "XMLHttpRequest",
                    ...(brandId ? { "Active-Brand": brandId } : {}),
                },
                body: JSON.stringify(request),
            })
                .then((res) => {
                    if (!res.ok) {
                        throw res;
                    }

                    return res.json();
                })
                .then((res) => {
                    sizeCurveResultCache.set(requestKey, res);
                    resolve(res);
                })
                .catch((error) => {
                    reject(error);
                });
        }
    });
};

const sentRequest = (
    merchantId: string,
    statement: object,
    from: ILookupFromType,
): Promise<{ error?: Error; result?: object[] }> => {
    const store = getStore();
    const { scope } = store.getState();

    return generateRequestPromise(scope.brand?._id, {
        merchant_id: merchantId,
        database_name: from.database_name,
        table_name: from.table_name,
        statement,
    })
        .then((result) => {
            return { result, error: undefined };
        })
        .catch((error) => ({ error, result: undefined }));
};

const getSchemaColumnNames = (fields: ILookupWhereField[], onlyRequired?: boolean) =>
    fields.reduce((acc, { column, is_required }) => {
        if ((onlyRequired && !is_required) || column.enforced_value) {
            return acc;
        }
        return [...acc, column.schema_column];
    }, []);

const getDependenciesFromWhereClause = ({ fields, fallback }: ILookupWhere, onlyRequired?: boolean): string[] => {
    const columnNames = getSchemaColumnNames(fields, onlyRequired);
    return Array.from(
        new Set(fallback ? columnNames.concat(getDependenciesFromWhereClause(fallback, onlyRequired)) : columnNames),
    );
};

export const getDependencies = (settings: ILookup, onlyRequired?: boolean) => (): string[] => {
    return getDependenciesFromWhereClause(settings.where, onlyRequired);
};

export const validateDependencies = (settings: ILookup) => (data: object) => {
    const requiredDependencies = getDependenciesFromWhereClause(settings.where, true);
    return requiredDependencies.some((key) => {
        const field = data[key];
        if (typeof field === "object") {
            return field?.string_value === "";
        }
        return nullish(field) || field === "";
    })
        ? formatMessage("Please enter a Department for this product")
        : "";
};

const throwErrorIfNeeded = (error: any) => {
    if (error && error.status >= 400) {
        throw error;
    }
};

const lookupSizeCurvesInternal = async (payload: ILookuoSizeCurvesParmas, settings: ILookup) => {
    try {
        const { data = {}, offset = 0, merchantId, fieldName, filterFn = () => true } = payload;
        const { rules, where } = verifyOrCalculateFilters(data, settings.where, payload.fieldToValueTypeMap);
        const columnLength = settings?.select?.terms?.length ?? 0;
        const statement = buildStatement({
            where: {
                rules,
                condition: "and",
            },
            select: get(settings, "select.terms", []).map((it) => ({
                column: it.column.table_column,
                ...(it.string_aggr
                    ? { function: { type: AggregationFunctions.StringAggregation, string_aggr: it.string_aggr } }
                    : {}),
            })),
            order: (settings?.order?.by ?? []).map((it) => ({ field: it.column, direction: it.sort.order })),
            groupBy: (settings?.group_by?.columns ?? []).map((it) => ({ column: it.table_column })),

            limit: recordsPerPage,
            offset,
        });

        const { error, result } = await sentRequest(merchantId, statement, settings.from);
        throwErrorIfNeeded(error);
        const pureSelectColumns = settings.select;
        const rows = !error && transformLookupResponse(result, pureSelectColumns).filter(filterFn);
        const isFirstPage = nullish(payload.offset) || payload.offset === 0;
        const rowsLength = rows && rows.length;
        const hasRows = !!rowsLength;
        if ((hasRows && rowsLength >= recordsPerPage) || (isFirstPage && !hasRows)) {
            const shouldRetry = isFirstPage && !hasRows;
            return await lookupSizeCurvesInternal(
                {
                    ...payload,
                    offset: hasRows ? (payload.offset || 0) + recordsPerPage : 0,
                    rows: shouldRetry ? [] : (payload.rows || []).concat(rows),
                },
                {
                    ...settings,
                    where: shouldRetry ? where.fallback : where,
                },
            );
        }
        const { returnKeyValue = true } = settings;
        const records = (payload.rows || []).concat(rows);
        if (returnKeyValue) {
            const keyIndex = fieldName
                ? 0
                : pureSelectColumns.terms.findIndex((item) => item.column.schema_column === fieldName);
            const combinedFormat = settings?.display?.format;
            const combinedDisplay = settings?.display;
            const keyValueRecords = records.map((row) => {
                // NOTE: the value is for display, if no datagetter pass.
                // what we display, what we store.
                // if no combine display, default will be the index 0 or 1.
                // row is the data from return result.
                return {
                    row,
                    key: getTransformedLookupValueByType(
                        (row as any)[pureSelectColumns.terms[keyIndex].column.table_column],
                    ),
                    value: combinedFormat
                        ? undefined
                        : getTransformedLookupValueByType(
                              (row as any)[pureSelectColumns.terms[columnLength > 0 ? 1 : 0].column.table_column],
                          ),
                    combinedDisplay,
                };
            });
            return keyValueRecords;
        } else {
            return records;
        }
    } catch (error) {
        // NOTE: prevent multiple api call.
        return [];
    }
};

export const lookupSizeCurves =
    (settings: ILookup, merchantId: string, fieldToValueTypeMap?: Map<string, ValueTypes>) =>
    async (payload: ILookuoSizeCurvesParmas) => {
        return lookupSizeCurvesInternal({ ...payload, merchantId, fieldToValueTypeMap }, settings);
    };

export const getLookupValueByType = (
    lookupRowValue: ILookupResponseRowValue,
    type: ILookupFieldType,
): number | string | undefined => {
    switch (type) {
        case ILookupFieldType.INT64:
            return lookupRowValue.int64_value?.value;
        case ILookupFieldType.STRING:
            return lookupRowValue.string_value?.value;
        default:
            return undefined;
    }
};

export const getTransformedLookupValueByType = (lookupRowValue: ILookupRowObject): number | string | undefined => {
    switch (lookupRowValue.type) {
        case ILookupFieldType.INT64:
            return lookupRowValue.int64_value;
        case ILookupFieldType.STRING:
            return lookupRowValue.string_value;
        default:
            return undefined;
    }
};

interface IInternalLookupResult {
    result?: any[];
    error?: any;
    isFirst: boolean;
}

const internalLookupCacheMap: {
    [key: string]: IDeferredPromise<IInternalLookupResult>[];
} = {};
// Only do single lookup with same cacheKey if same lookups are rapidly executed.
export const debouncedLookupSizeCurveInternal = (
    interalLookup,
    dependency: any,
    fieldName: string,
    cacheKey: string,
) => {
    const promise = deferredPromise<IInternalLookupResult>();
    if (!internalLookupCacheMap[cacheKey]) {
        internalLookupCacheMap[cacheKey] = [promise];
        interalLookup({ data: dependency, fieldName })
            .then((result) => {
                internalLookupCacheMap[cacheKey].forEach((queue, index) => {
                    queue.resolve({
                        result,
                        isFirst: index === 0,
                    });
                });

                // Clear queue
                delete internalLookupCacheMap[cacheKey];
            })
            .catch((error) => {
                internalLookupCacheMap[cacheKey].forEach((queue, index) => {
                    queue.reject({
                        error,
                        isFirst: index === 0,
                    });
                });

                // Clear queue
                delete internalLookupCacheMap[cacheKey];
            });
    } else {
        internalLookupCacheMap[cacheKey].push(promise);
    }

    return promise;
};

export const transformDataToSuggestions = (result, dataFields) => {
    const returnData = result.slice();
    let lookupSchemaNames = [];

    if (result?.length > 0 && result[0].combinedDisplay) {
        // NOTE: if we have combineFormat, compile with data in line item and return data.
        const displayFormatObj = result[0].combinedDisplay;
        const lookupData = {};
        const compiledDisplayObj = Object.keys(displayFormatObj).reduce((acc, key) => {
            return {
                ...acc,
                [key]: template(displayFormatObj[key], { variable: VARIABLE_NAME }),
            };
        }, {});
        // get data from the item
        Object.keys(dataFields).forEach((key) => {
            lookupData[key] = dataFields[key].string_value;
        });
        returnData.forEach((data, index) => {
            const baseLookupData = lookupData;
            const rowKeys = Object.keys(data.row);
            // get data from the return data.
            rowKeys.forEach((key) => {
                const schemaKey = data.row[key].schemaName;
                baseLookupData[schemaKey] = getTransformedLookupValueByType(data.row[key]);
            });
            // NOTE: Because The value will be store in redux for both editor and renderer.
            //  Compile both and store in cache.
            Object.keys(compiledDisplayObj).forEach((key) => {
                const formatValue = compiledDisplayObj[key](baseLookupData);
                returnData[index][key] = formatValue;
            });
            const dropdownFormat = data.combinedDisplay.dropdown_format;
            if (dropdownFormat) {
                const replacedDropdownFormat = dropdownFormat
                    .replaceAll("<%= ", "")
                    .replaceAll(" %>", "")
                    .replaceAll("lookup.", "");
                let separator = SEMICOLON_DELIMITER;
                if (replacedDropdownFormat.includes(COMMA_DELIMITER)) {
                    separator = COMMA_DELIMITER;
                }
                lookupSchemaNames = replacedDropdownFormat.split(separator);
            }
        });
    }

    return { suggestions: returnData, lookupSchemaNames };
};

export const VARIABLE_NAME = "lookup";
export const generateStoreKey = (lookupPlan: ILookup, dependency: IDependency, currentColumn: string) => {
    const keyObject = {
        ...dependency,
    };
    const { database_name, table_name } = lookupPlan.from;
    const root = `${database_name}-${table_name}-${currentColumn}`;
    const dependencyString = Object.keys(keyObject)
        .map((key) => `${key}|${keyObject[key]}`)
        .join("_");
    return `${root}-${dependencyString}`;
};

export const fetchSuggestionsFromInternalLookup = async ({ fieldSchema, merchantId, rawItemDisplayProperty }) => {
    const { interalLookup, dependency } = internalGetter(fieldSchema, merchantId, rawItemDisplayProperty)({});
    const result = await interalLookup({ data: dependency, fieldName: fieldSchema.field_name });

    return transformDataToSuggestions(result, {});
};
